import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Users,
  Dumbbell,
  Utensils,
  BookOpen,
  RefreshCw,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { getRecentActivityAction } from "../../../store/slices/analytics/analyticsSliceActions";
import {
  setSelectedActivityWeeks,
  clearError,
} from "../../../store/slices/analytics/analyticsSlice";
import { API_STATUS } from "../../../constants/apiStatus";
import { colors } from "../../../constants/colors";

const RecentActivitySection = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { recentActivity, selectedActivityWeeks, apiStatus, error } =
    useAppSelector((state) => state.analytics);

  const isLoading = apiStatus.getRecentActivity === API_STATUS.STARTED;
  const hasData = recentActivity !== null;

  // Don't auto-load on mount - wait for user to click refresh
  // This prevents errors from showing if the endpoint doesn't exist yet
  useEffect(() => {
    // Only load if we already have data (e.g., after week selection change)
    if (hasData && selectedActivityWeeks) {
      dispatch(getRecentActivityAction(selectedActivityWeeks));
    }
  }, [dispatch, selectedActivityWeeks, hasData]);

  // Handle week selection change
  const handleWeekChange = (e) => {
    const weeks = parseInt(e.target.value);
    dispatch(clearError()); // Clear any previous errors
    dispatch(setSelectedActivityWeeks(weeks));
  };

  // Refresh data
  const handleRefresh = () => {
    dispatch(clearError()); // Clear any previous errors before fetching
    dispatch(getRecentActivityAction(selectedActivityWeeks));
  };

  // Format date - handle various date formats including "2025-09-30_15:08"
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      // Handle format like "2025-09-30_15:08"
      if (typeof dateString === "string" && dateString.includes("_")) {
        const [datePart, timePart] = dateString.split("_");
        const isoString = `${datePart}T${timePart}:00`;
        return new Date(isoString).toLocaleString();
      }
      // Handle ISO string or timestamp
      return new Date(dateString).toLocaleString();
    } catch (err) {
      console.error("Error formatting date:", err);
      return dateString;
    }
  };

  // Navigate to user detail page
  const handleUserClick = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  // Styles
  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "2rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    marginBottom: "2rem",
  };

  const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.5rem",
    flexWrap: "wrap",
    gap: "1rem",
  };

  const titleStyle = {
    fontSize: "1.5rem",
    fontWeight: "700",
    color: colors.darkBlue,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const controlsStyle = {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  };

  const selectStyle = {
    padding: "0.75rem",
    borderRadius: "8px",
    border: `1px solid ${colors.lightGray}`,
    backgroundColor: colors.white,
    color: colors.darkBlue,
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: "pointer",
    outline: "none",
  };

  const refreshButtonStyle = {
    padding: "0.75rem 1rem",
    borderRadius: "8px",
    border: "none",
    backgroundColor: colors.darkGreen,
    color: colors.white,
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: isLoading ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    opacity: isLoading ? 0.6 : 1,
    transition: "all 0.3s ease",
  };

  const summaryGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "1rem",
    marginBottom: "2rem",
  };

  const summaryCardStyle = {
    backgroundColor: colors.extraLightGray,
    borderRadius: "12px",
    padding: "1.5rem",
    border: `1px solid ${colors.doubleExtraLightGray}`,
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  };

  const summaryValueStyle = {
    fontSize: "2rem",
    fontWeight: "700",
    color: colors.darkBlue,
  };

  const summaryLabelStyle = {
    fontSize: "0.875rem",
    color: colors.darkGray,
    fontWeight: "600",
  };

  const activityTableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "1rem",
  };

  const tableHeaderStyle = {
    backgroundColor: colors.extraLightGray,
    padding: "1rem",
    textAlign: "left",
    fontSize: "0.875rem",
    fontWeight: "600",
    color: colors.darkGray,
    borderBottom: `2px solid ${colors.lightGray}`,
  };

  const tableRowStyle = {
    borderBottom: `1px solid ${colors.doubleExtraLightGray}`,
  };

  const tableCellStyle = {
    padding: "1rem",
    fontSize: "0.875rem",
    color: colors.darkBlue,
  };

  const userIdLinkStyle = {
    fontFamily: "monospace",
    fontSize: "0.8rem",
    backgroundColor: colors.extraLightGray,
    padding: "0.25rem 0.5rem",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "inline-block",
    color: colors.darkBlue,
    textDecoration: "none",
    border: `1px solid transparent`,
  };

  const userIdLinkHoverStyle = {
    backgroundColor: colors.darkGreen,
    color: colors.white,
    border: `1px solid ${colors.darkGreen}`,
  };

  // Don't show error as full component - show it inline instead
  // This prevents the whole UI from breaking

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={titleStyle}>
          <Activity size={24} />
          Recent User Activity
        </div>

        <div style={controlsStyle}>
          <select
            value={selectedActivityWeeks}
            onChange={handleWeekChange}
            style={selectStyle}
            disabled={isLoading}
          >
            {[
              1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
              20,
            ].map((weeks) => (
              <option key={weeks} value={weeks}>
                {weeks} {weeks === 1 ? "Week" : "Weeks"}
              </option>
            ))}
          </select>

          <button
            onClick={handleRefresh}
            style={refreshButtonStyle}
            disabled={isLoading}
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && !isLoading && (
        <div
          style={{
            backgroundColor: "#fee2e2",
            color: "#dc2626",
            padding: "1rem",
            borderRadius: "8px",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "1rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "0.5rem",
              flex: 1,
            }}
          >
            <AlertCircle
              size={20}
              style={{ marginTop: "0.125rem", flexShrink: 0 }}
            />
            <div>
              <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>
                Unable to Load Recent Activity
              </div>
              <div style={{ fontSize: "0.875rem", lineHeight: "1.5" }}>
                {error.includes("404")
                  ? "The recent activity endpoint is not yet deployed on the server. Please ensure the backend route is added and deployed."
                  : `Error: ${error}`}
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  marginTop: "0.5rem",
                  opacity: 0.8,
                }}
              >
                💡 Click the refresh button to try again once the endpoint is
                available.
              </div>
            </div>
          </div>
          <button
            onClick={() => dispatch(clearError())}
            style={{
              background: "transparent",
              border: "none",
              color: "#dc2626",
              cursor: "pointer",
              padding: "0.25rem",
              fontSize: "1.25rem",
              lineHeight: 1,
              fontWeight: "bold",
            }}
            title="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            color: colors.darkGray,
          }}
        >
          <RefreshCw
            size={32}
            color={colors.darkGray}
            className="animate-spin"
            style={{ margin: "0 auto 1rem" }}
          />
          <div>Loading activity data...</div>
        </div>
      )}

      {/* Content */}
      {!isLoading && recentActivity && (
        <>
          {/* Period Info */}
          {recentActivity.period && (
            <div
              style={{
                backgroundColor: "#e0f2fe",
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1.5rem",
                fontSize: "0.875rem",
                color: "#0369a1",
                fontWeight: "600",
              }}
            >
              📅 Period: {formatDate(recentActivity.period.startDate)} →{" "}
              {formatDate(recentActivity.period.endDate)} (
              {recentActivity.period.days} days)
            </div>
          )}

          {/* Summary Cards */}
          {recentActivity.summary && (
            <div style={summaryGridStyle}>
              <div style={summaryCardStyle}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Users size={20} color={colors.darkGreen} />
                  <span style={summaryLabelStyle}>Unique Active Users</span>
                </div>
                <div style={{ ...summaryValueStyle, color: colors.darkGreen }}>
                  {recentActivity.summary.uniqueUsers}
                </div>
              </div>

              <div style={summaryCardStyle}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Dumbbell size={20} color="#3b82f6" />
                  <span style={summaryLabelStyle}>Workout Plans Created</span>
                </div>
                <div style={{ ...summaryValueStyle, color: "#3b82f6" }}>
                  {recentActivity.summary.totalWorkoutPlans}
                </div>
              </div>

              <div style={summaryCardStyle}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Utensils size={20} color="#f59e0b" />
                  <span style={summaryLabelStyle}>Meals Created</span>
                </div>
                <div style={{ ...summaryValueStyle, color: "#f59e0b" }}>
                  {recentActivity.summary.totalMeals}
                </div>
              </div>

              <div style={summaryCardStyle}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <BookOpen size={20} color="#8b5cf6" />
                  <span style={summaryLabelStyle}>Recipes Saved</span>
                </div>
                <div style={{ ...summaryValueStyle, color: "#8b5cf6" }}>
                  {recentActivity.summary.totalRecipes}
                </div>
              </div>
            </div>
          )}

          {/* Activity Details */}
          {recentActivity.activity && (
            <div>
              {/* Workout Plans Activity */}
              {recentActivity.activity.workoutPlans &&
                recentActivity.activity.workoutPlans.length > 0 && (
                  <div style={{ marginBottom: "2rem" }}>
                    <h3
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        color: colors.darkBlue,
                        marginBottom: "1rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Dumbbell size={20} color="#3b82f6" />
                      Workout Plans Activity (
                      {recentActivity.activity.workoutPlans.length})
                    </h3>
                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        border: `1px solid ${colors.lightGray}`,
                        borderRadius: "8px",
                      }}
                    >
                      <table style={activityTableStyle}>
                        <thead>
                          <tr>
                            <th style={tableHeaderStyle}>User ID</th>
                            <th style={tableHeaderStyle}>Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentActivity.activity.workoutPlans.map(
                            (activity, idx) => (
                              <tr key={idx} style={tableRowStyle}>
                                <td style={tableCellStyle}>
                                  <span
                                    onClick={() =>
                                      handleUserClick(activity.userId)
                                    }
                                    style={userIdLinkStyle}
                                    onMouseEnter={(e) => {
                                      Object.assign(
                                        e.target.style,
                                        userIdLinkHoverStyle
                                      );
                                    }}
                                    onMouseLeave={(e) => {
                                      Object.assign(
                                        e.target.style,
                                        userIdLinkStyle
                                      );
                                    }}
                                    title={`View user ${activity.userId}`}
                                  >
                                    {activity.userId}
                                  </span>
                                </td>
                                <td style={tableCellStyle}>
                                  {formatDate(activity.createdAt)}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {/* Meals Activity */}
              {recentActivity.activity.meals &&
                recentActivity.activity.meals.length > 0 && (
                  <div style={{ marginBottom: "2rem" }}>
                    <h3
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        color: colors.darkBlue,
                        marginBottom: "1rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Utensils size={20} color="#f59e0b" />
                      Meals Activity ({recentActivity.activity.meals.length})
                    </h3>
                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        border: `1px solid ${colors.lightGray}`,
                        borderRadius: "8px",
                      }}
                    >
                      <table style={activityTableStyle}>
                        <thead>
                          <tr>
                            <th style={tableHeaderStyle}>User ID</th>
                            <th style={tableHeaderStyle}>Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentActivity.activity.meals.map(
                            (activity, idx) => (
                              <tr key={idx} style={tableRowStyle}>
                                <td style={tableCellStyle}>
                                  <span
                                    onClick={() =>
                                      handleUserClick(activity.userId)
                                    }
                                    style={userIdLinkStyle}
                                    onMouseEnter={(e) => {
                                      Object.assign(
                                        e.target.style,
                                        userIdLinkHoverStyle
                                      );
                                    }}
                                    onMouseLeave={(e) => {
                                      Object.assign(
                                        e.target.style,
                                        userIdLinkStyle
                                      );
                                    }}
                                    title={`View user ${activity.userId}`}
                                  >
                                    {activity.userId}
                                  </span>
                                </td>
                                <td style={tableCellStyle}>
                                  {formatDate(activity.createdAt)}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {/* Recipes Activity */}
              {recentActivity.activity.recipes &&
                recentActivity.activity.recipes.length > 0 && (
                  <div>
                    <h3
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        color: colors.darkBlue,
                        marginBottom: "1rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <BookOpen size={20} color="#8b5cf6" />
                      Recipes Activity ({recentActivity.activity.recipes.length}
                      )
                    </h3>
                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        border: `1px solid ${colors.lightGray}`,
                        borderRadius: "8px",
                      }}
                    >
                      <table style={activityTableStyle}>
                        <thead>
                          <tr>
                            <th style={tableHeaderStyle}>User ID</th>
                            <th style={tableHeaderStyle}>Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentActivity.activity.recipes.map(
                            (activity, idx) => (
                              <tr key={idx} style={tableRowStyle}>
                                <td style={tableCellStyle}>
                                  <span
                                    onClick={() =>
                                      handleUserClick(activity.userId)
                                    }
                                    style={userIdLinkStyle}
                                    onMouseEnter={(e) => {
                                      Object.assign(
                                        e.target.style,
                                        userIdLinkHoverStyle
                                      );
                                    }}
                                    onMouseLeave={(e) => {
                                      Object.assign(
                                        e.target.style,
                                        userIdLinkStyle
                                      );
                                    }}
                                    title={`View user ${activity.userId}`}
                                  >
                                    {activity.userId}
                                  </span>
                                </td>
                                <td style={tableCellStyle}>
                                  {formatDate(activity.createdAt)}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {/* No Activity Message */}
              {(!recentActivity.activity.workoutPlans ||
                recentActivity.activity.workoutPlans.length === 0) &&
                (!recentActivity.activity.meals ||
                  recentActivity.activity.meals.length === 0) &&
                (!recentActivity.activity.recipes ||
                  recentActivity.activity.recipes.length === 0) && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "3rem",
                      color: colors.darkGray,
                      backgroundColor: colors.extraLightGray,
                      borderRadius: "8px",
                    }}
                  >
                    <TrendingUp
                      size={48}
                      color={colors.lightGray}
                      style={{ margin: "0 auto 1rem" }}
                    />
                    <div>No activity found for the selected period.</div>
                  </div>
                )}
            </div>
          )}
        </>
      )}

      {/* No Data Message */}
      {!isLoading && !recentActivity && !error && (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            color: colors.darkGray,
            backgroundColor: colors.extraLightGray,
            borderRadius: "8px",
          }}
        >
          <Activity
            size={48}
            color={colors.lightGray}
            style={{ margin: "0 auto 1rem" }}
          />
          <div
            style={{
              fontSize: "1.1rem",
              fontWeight: "600",
              marginBottom: "0.5rem",
            }}
          >
            No Activity Data Loaded
          </div>
          <div style={{ fontSize: "0.875rem" }}>
            Click the refresh button to load recent user activity data.
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentActivitySection;
